import { useState, type ReactNode } from 'react'
import toString from 'lodash/toString'
import { Download } from 'lucide-react'
import { Link } from 'react-router'
import { ROUTE_DOCUMENT_REVIEW, ROUTE_ORDER_EVENTS, ROUTE_ORDERS, ROUTE_PAYMENT } from '@klear/constants'
import { CalendarQuarter } from '@klear/date/calendar-quarter'
import type { DocumentListRow, DocumentReference, TraderDocument } from '@klear/document/trader-document'
import { DOCUMENT_TYPE_LABEL, DOCUMENT_TYPE_UNCLASSIFIED } from '@klear/constants/document-types'
import { DocumentViewButton } from '@klear/ui/components/document-view-button'
import { Button } from '@klear/ui/components/button'
import ImgPreviewModal from '@klear/ui/components/img-preview-modal'
import PdfPreviewModal from '@klear/ui/components/pdf-preview-modal'
import { useFileDownload } from '@klear/ui/lib/download-file'
import { cn } from '@klear/ui/lib/utils'
import { NOT_PREVIEWABLE_MESSAGE } from '@klear/upload/content-type'
import { buildDocumentFileUrl, withAttachmentDisposition } from '@klear/upload/file-url'

const LINK = 'underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground'

export const typeLabel = (document: TraderDocument) =>
  document.type === DOCUMENT_TYPE_UNCLASSIFIED ? '—' : DOCUMENT_TYPE_LABEL[document.type]

export const addedOn = (document: TraderDocument) =>
  new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(document.arrivedAt.epochMs)

export function captionOf({ document }: DocumentListRow): string | undefined {
  if (document.arrivedVia === 'email') return 'Emailed'
  return document.notes === '' ? undefined : document.notes
}

function ReferenceLink({ reference }: { reference: DocumentReference }) {
  if (reference.kind === 'financial_statement') {
    return <span>Financial statement · {CalendarQuarter.of(reference.periodEnd).label}</span>
  }
  if (reference.kind === 'payment') {
    return (
      <Link to={`/${ROUTE_PAYMENT}/${reference.id}`} className={LINK}>
        {reference.displayId}
      </Link>
    )
  }
  return (
    <span>
      {reference.orderDisplayId !== null && (
        <>
          <Link to={`/${ROUTE_ORDERS}/${reference.orderDisplayId}`} className={LINK}>
            {reference.orderDisplayId}
          </Link>
          <span className="text-muted-foreground"> · </span>
        </>
      )}
      <Link to={`/${ROUTE_ORDER_EVENTS}/${reference.displayId}`} className={LINK}>
        {reference.displayId}
      </Link>
    </span>
  )
}

export function WhereCell({
  detail,
  references = detail.document.references,
  empty = <span className="text-muted-foreground">—</span>,
  className
}: {
  detail: DocumentListRow
  references?: readonly DocumentReference[]
  empty?: ReactNode
  className?: string
}) {
  const run = detail.latestRun
  const unprocessed = run === null && detail.document.status === 'processing' ? detail.document.id : null
  if (run === null && unprocessed === null && references.length === 0) return empty
  return (
    <ul className={cn('flex flex-col gap-0.5', className)}>
      {run !== null && (
        <li className="whitespace-nowrap">
          <Link to={`/${ROUTE_DOCUMENT_REVIEW}/${run.id}`} className={LINK}>
            review {run.id}
          </Link>
        </li>
      )}
      {unprocessed !== null && (
        <li className="whitespace-nowrap">
          <Link to={`/${ROUTE_DOCUMENT_REVIEW}/document/${unprocessed}`} className={LINK}>
            not processed
          </Link>
        </li>
      )}
      {references.map((reference) => (
        <li key={`${reference.kind}:${reference.id}`} className="whitespace-nowrap">
          <ReferenceLink reference={reference} />
        </li>
      ))}
    </ul>
  )
}

export function FileActions({ document }: { document: TraderDocument }) {
  const [previewOpen, setPreviewOpen] = useState(false)
  const { startDownload, isDownloading } = useFileDownload()
  const url = buildDocumentFileUrl(toString(document.id))
  const attachmentUrl = withAttachmentDisposition(url)
  return (
    <div className="flex items-center justify-end gap-0.5">
      <DocumentViewButton
        name={document.name}
        unavailableReason={document.isPdf || document.isImage ? null : NOT_PREVIEWABLE_MESSAGE}
        onView={() => setPreviewOpen(true)}
      />
      <Button
        variant="ghost"
        className="h-8 w-8 p-0"
        onClick={() => startDownload(attachmentUrl, document.name)}
        disabled={isDownloading}
        title="Download"
      >
        <Download className="h-4 w-4" />
        <span className="sr-only">Download {document.name}</span>
      </Button>
      {document.isPdf && (
        <PdfPreviewModal
          title={document.name}
          pdfUrl={url}
          downloadUrl={attachmentUrl}
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          downloadable
        />
      )}
      {document.isImage && (
        <ImgPreviewModal
          fileUrl={url}
          downloadUrl={attachmentUrl}
          fileName={document.name}
          open={previewOpen}
          onOpenChange={setPreviewOpen}
        />
      )}
    </div>
  )
}
